/**
 * AlarmEditDialog.tsx — edit an existing alarm in a dialog.
 * Loads the alarm's conditions via inverse parser → rows.
 * Falls back to raw-JSON editor if conditions are unparseable.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Switch,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useAlarmFieldCatalogByType,
  useUpdateAlarm,
  useWatchpoint,
  useChannels,
  useAlarmChannels,
  useLinkAlarmChannel,
  useUnlinkAlarmChannel,
  usePlaybooks,
  useSetAlarmPlaybook,
  type Alarm,
  type AlarmSeverity,
  type WatchpointType,
} from '@/api/healthcheck';
import { hcExtractError } from '@/api/healthcheck';
import { parseConditions, serializeConditions, validateRows, recoerceRows } from './condition-serializer';
import type { ConditionRow } from './condition-serializer';
import { ConditionBuilder } from './ConditionBuilder';
import { ChannelMultiselect } from './ChannelMultiselect';

const SEVERITIES: AlarmSeverity[] = ['info', 'warning', 'critical'];

interface Props {
  alarm: Alarm | null;
  onClose: () => void;
}

export function AlarmEditDialog({ alarm, onClose }: Props) {
  return (
    <Dialog open={!!alarm} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        {alarm ? (
          <AlarmEditForm alarm={alarm} onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function AlarmEditForm({ alarm, onClose }: { alarm: Alarm; onClose: () => void }) {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: wp } = useWatchpoint(alarm.watchpoint_id);
  const wpType = wp?.type as WatchpointType | undefined;
  const { data: catalog, isLoading: catalogLoading } = useAlarmFieldCatalogByType(wpType);
  const { data: channels = [] } = useChannels();
  const { data: linkedChannels = [] } = useAlarmChannels(alarm.id);
  const { data: playbooks = [] } = usePlaybooks(projectId);
  const setAlarmPlaybook = useSetAlarmPlaybook();

  const update = useUpdateAlarm(alarm.id, { onSuccess: onClose });
  const linkChannel = useLinkAlarmChannel(alarm.id);
  const unlinkChannel = useUnlinkAlarmChannel(alarm.id);

  // ---- Form state (seeded from alarm) ------------------------------------
  const [name, setName] = useState(alarm.name);
  const [severity, setSeverity] = useState<AlarmSeverity>(alarm.severity);
  const [consecutiveFailures, setConsecutiveFailures] = useState(alarm.consecutive_failures);
  const [cooldownMinutes, setCooldownMinutes] = useState(alarm.cooldown_minutes);
  const [notifyOnRecovery, setNotifyOnRecovery] = useState(alarm.notify_on_recovery ?? true);
  const [rows, setRows] = useState<ConditionRow[]>([]);
  const [mode, setMode] = useState<'and' | 'or'>('and');
  const [rawFallback, setRawFallback] = useState<string | null>(null);
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [playbookId, setPlaybookId] = useState<string>(alarm.playbook_id ?? '');
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Seed channel IDs from linked channels once loaded
  useEffect(() => {
    setChannelIds(linkedChannels.map((c) => c.id));
  }, [linkedChannels]);

  // Parse conditions once catalog arrives
  useEffect(() => {
    if (!catalog) return;
    const parsed = parseConditions(alarm.conditions);
    if (parsed.rawFallback !== null) {
      setRawFallback(parsed.rawFallback);
      setRows([]);
    } else {
      setRows(parsed.rows);
      setMode(parsed.mode);
      setRawFallback(null);
    }
  }, [catalog, alarm.conditions]);

  const validate = (): boolean => {
    setShowErrors(true);
    if (!name.trim()) return false;
    if (rawFallback !== null) return rawFallback.trim().length > 0;
    return validateRows(rows).length === 0 && consecutiveFailures >= 1 && cooldownMinutes >= 0;
  };

  const handleSave = async () => {
    setSubmitError(null);
    if (!validate()) return;

    let conditions: unknown;
    if (rawFallback !== null) {
      try { conditions = JSON.parse(rawFallback); }
      catch { setSubmitError('Invalid JSON in conditions.'); return; }
    } else {
      // H1: re-coerce all row values by catalog field.kind before serialising.
      // Handles externally-written or raw-JSON-edited values that may be strings.
      const fields = catalog?.condition_fields ?? [];
      const coercedRows = recoerceRows(rows, fields);
      const revalidationErrors = validateRows(coercedRows);
      if (revalidationErrors.length > 0) {
        setSubmitError(revalidationErrors.map((e) => e.message).join(' '));
        return;
      }
      // catalog is non-null here: validate() returned true which required !rawFallback path
      conditions = serializeConditions(coercedRows, catalog?.eval_level ?? 'probe_result', mode);
    }

    // Diff channel links: add new, remove removed
    const previousIds = linkedChannels.map((c) => c.id);
    const toAdd = channelIds.filter((id) => !previousIds.includes(id));
    const toRemove = previousIds.filter((id) => !channelIds.includes(id));

    // Diff playbook attachment
    const newPlaybookId = playbookId || null;
    const oldPlaybookId = alarm.playbook_id ?? null;
    const playbookChanged = newPlaybookId !== oldPlaybookId;

    try {
      await Promise.all([
        ...toAdd.map((id) => linkChannel.mutateAsync(id)),
        ...toRemove.map((id) => unlinkChannel.mutateAsync(id)),
        ...(playbookChanged ? [setAlarmPlaybook.mutateAsync({ alarmId: alarm.id, playbookId: newPlaybookId })] : []),
      ]);
    } catch (e) {
      setSubmitError(hcExtractError(e, 'Channel or playbook update failed'));
      return;
    }

    update.mutate(
      { name: name.trim(), severity, conditions, consecutive_failures: consecutiveFailures, cooldown_minutes: cooldownMinutes, notify_on_recovery: notifyOnRecovery },
      { onError: (e) => setSubmitError(hcExtractError(e, 'Update failed')) },
    );
  };

  const pending = update.isPending || linkChannel.isPending || unlinkChannel.isPending || setAlarmPlaybook.isPending;
  const selectedChannelNames = channelIds
    .map((id) => channels.find((c) => c.id === id)?.name ?? '')
    .filter(Boolean);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit alarm — {alarm.name}</DialogTitle>
      </DialogHeader>

      <DialogBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Name */}
          <Field
            label="Name"
            required
            error={showErrors && !name.trim() ? 'Name is required.' : undefined}
          >
            {(p) => (
              <Input
                {...p}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                invalid={showErrors && !name.trim()}
              />
            )}
          </Field>

          {/* Conditions */}
          <div>
            <p style={{ margin: '0 0 var(--vn-space-2)', fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
              Conditions
              {wp && (
                <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400, marginLeft: 6 }}>
                  ({wp.type})
                </span>
              )}
            </p>
            {catalogLoading ? (
              <Skeleton style={{ height: 80 }} />
            ) : catalog ? (
              <ConditionBuilder
                catalog={catalog}
                rows={rows}
                mode={mode}
                onRowsChange={setRows}
                onModeChange={setMode}
                showErrors={showErrors}
                rawFallback={rawFallback ?? undefined}
                onRawFallbackChange={setRawFallback}
              />
            ) : (
              <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                Loading catalog…
              </p>
            )}
          </div>

          {/* Severity + thresholds */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--vn-space-3)' }}>
            <Field label="Severity">
              {() => (
                <Select value={severity} onValueChange={(v) => setSeverity(v as AlarmSeverity)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s} style={{ textTransform: 'capitalize' }}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field
              label="Consecutive failures"
              error={showErrors && consecutiveFailures < 1 ? 'Min 1.' : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={1}
                  value={String(consecutiveFailures)}
                  onChange={(e) => setConsecutiveFailures(Math.max(1, Number(e.target.value) || 1))}
                  invalid={showErrors && consecutiveFailures < 1}
                />
              )}
            </Field>
            <Field
              label="Cooldown (min)"
              error={showErrors && cooldownMinutes < 0 ? 'Min 0.' : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={0}
                  value={String(cooldownMinutes)}
                  onChange={(e) => setCooldownMinutes(Math.max(0, Number(e.target.value) || 0))}
                  invalid={showErrors && cooldownMinutes < 0}
                />
              )}
            </Field>
          </div>

          {/* Recovery notification toggle */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--vn-space-3)',
              cursor: 'pointer',
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
                Send &ldquo;Recovered&rdquo; notification
              </div>
              <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                Notify channels when this alarm clears. Off = only alert when it fires.
              </div>
            </div>
            <Switch checked={notifyOnRecovery} onCheckedChange={setNotifyOnRecovery} />
          </label>

          {/* Channels */}
          <div>
            <p style={{ margin: '0 0 var(--vn-space-2)', fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
              Notification channels
            </p>
            <ChannelMultiselect value={channelIds} onChange={setChannelIds} />
            {selectedChannelNames.length > 0 && (
              <p style={{ margin: 'var(--vn-space-1) 0 0', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                Selected: {selectedChannelNames.join(', ')}
              </p>
            )}
          </div>

          {/* Playbook */}
          <div>
            <p style={{ margin: '0 0 var(--vn-space-2)', fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
              Auto-remediation playbook (optional)
            </p>
            <Select
              value={playbookId || '__none__'}
              onValueChange={(v) => setPlaybookId(v === '__none__' ? '' : v)}
              disabled={playbooks.length === 0}
            >
              <SelectTrigger style={{ maxWidth: 360 }}>
                <SelectValue placeholder="— None —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {playbooks.map((pb) => (
                  <SelectItem key={pb.id} value={pb.id}>
                    {pb.name}{pb.is_active ? ' (Armed)' : ' (Disarmed)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {playbooks.length === 0 && (
              <p style={{ margin: 'var(--vn-space-1) 0 0', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                No playbooks yet — create one from the Playbooks page.
              </p>
            )}
          </div>

          {submitError && <Alert tone="danger" title={submitError} />}
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={pending}>
          {pending ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogFooter>
    </>
  );
}
